import { Link } from "react-router-dom";

import { Car } from "@mywheels/common/lib/cars/types";
import {
  addTranslationData,
  useTranslation,
} from "@mywheels/common/lib/translation";
import {
  StringParam,
  useQueryParam,
} from "@mywheels/common/util/useQueryParams";
import {
  GraphCMSQueryDef,
  useGraphCMS,
} from "@mywheels/common/lib/useGraphCMS";

import { Spinner } from "web/ui";
import { useRoutes } from "web/lib/i18n";
import { GeoPinIcon } from "web/ui/icons/GeoPinIcon";
import { GeoZoneIcon } from "web/ui/icons/GeoZoneIcon";

import { ExpandableContent } from "./ExpandableContent";
import { ManualSectionLink } from "./ManualSectionLink";

import { GraphCmsManualItem } from "./types";

addTranslationData({
  en: {
    Inhoudsopgave: "Table of contents",
  },
});

// The reason for accessing the localized name/title instead of just retrieving the object localized in the first place, is because the car NULL check will be a false positive when accessed from the localized section, because somehow localized items don't associate well, or there's something with the published status or something.., eventually leading to retrieving all cars' manual items under this section..

const GetManualSectionQuery: GraphCMSQueryDef<
  {
    manualSection: {
      id: string;
      name: string;
      localizations: Array<{
        name: string;
      }>;
      slug: string;
      manualItems: GraphCmsManualItem[];
    };
  },
  { manualSectionSlug: string; carSlug: string; locale: string }
> = {
  query: `
    query GetManualSectionQuery($manualSectionSlug: String!, $carSlug: String!, $locale: Locale!) {
      manualSection(where: { slug: $manualSectionSlug }) {
        id
        slug
        name
        localizations(locales: [$locale]){
          name
        }
        manualItems(where: {
          OR: [
            { car: null },
            { car: { slug: $carSlug } }
          ]
        }, orderBy: order_ASC) {
          id
          title
          localizations(locales: [$locale]){
            title
          }
          parkingType
        }
      }
    }
  `,
};

export function ManualSectionContent({
  car,
  manualSectionSlug,
}: {
  car: Car;
  manualSectionSlug: string;
}) {
  const { t, language } = useTranslation();
  const routes = useRoutes();

  const [parkingType] = useQueryParam("parking_type", StringParam);

  const { data } = useGraphCMS(GetManualSectionQuery, {
    carSlug: car.id,
    manualSectionSlug,
    locale: language,
  });

  if (!data?.manualSection) {
    return (
      <div className="container my-3 md:my-6">
        <Spinner className="mx-auto" />
      </div>
    );
  }

  const { manualSection } = data;

  return (
    <div className="container my-3 md:my-6">
      <div className="row">
        <div className="md:col-1" />
        <div className="col-12 md:col-10">
          <div className="mb-5 text-base text-center">
            <Link
              to={routes.CarManual.url({ carId: car.id })}
              className="text-gray underline font-sans"
            >
              {t("Inhoudsopgave")}
            </Link>
            <span className="text-gray mx-1">{" > "}</span>
            <h2 className="inline text-base font-sans font-bold">
              {manualSection.localizations[0]?.name ?? manualSection.name}
            </h2>
          </div>

          {manualSection.slug === "parkeren" && !parkingType ? (
            <div>
              <div className="my-6 mx-10 text-center text-gray md:text-lg">
                {t("Kies het parkeertype van de auto die je huurt:")}
              </div>
              <ManualSectionLink
                search="?parking_type=parking_spot"
                carId={car.id}
                sectionId="parkeren"
                Icon={GeoPinIcon}
              >
                {t("Vaste plek")}
              </ManualSectionLink>
              <ManualSectionLink
                search="?parking_type=zone"
                carId={car.id}
                sectionId="parkeren"
                Icon={GeoZoneIcon}
              >
                {t("Zoneplek")}
              </ManualSectionLink>
            </div>
          ) : manualSection.manualItems.length === 0 ? (
            <p className="p-5 text-muted text-lg text-center">
              {t(
                "Deze sectie is helaas nog leeg, maar wordt binnenkort gevuld!"
              )}
            </p>
          ) : (
            manualSection.manualItems
              .filter((item) => {
                return (
                  manualSection.slug !== "parkeren" ||
                  !item.parkingType ||
                  item.parkingType === parkingType
                );
              })
              .map((item) => {
                return (
                  <div key={item.id} className="-mx-3">
                    <ExpandableContent
                      title={item.localizations[0]?.title ?? item.title}
                      car={car}
                      manualSectionSlug={manualSectionSlug}
                      item={item}
                    />
                  </div>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
}
